import { httpsCallable, functions, db } from '@/services/firebase/config'
import { doc, onSnapshot } from 'firebase/firestore'
import { Notify } from 'quasar'
import { sanitizeHtml } from '@/utils/sanitizeHtml'
import { handleQuotaError } from './quotaErrorHandler'
import StorageQuotaService from './storage/StorageQuotaService'
import { useUserStore } from '@/stores/userStore'

/**
 * Calculate estimated output size for AI requests that generate media
 * @param {string} purpose - AI job purpose
 * @returns {number} Estimated size in bytes
 */
function estimateOutputSize(purpose) {
  // Conservative estimates for AI-generated media outputs
  const estimates = {
    characterAvatar: 500 * 1024, // ~500KB for avatar image
    characterGallery: 800 * 1024, // ~800KB for gallery image
    campaignImage: 1024 * 1024, // ~1MB for campaign image
    audioGeneration: 2 * 1024 * 1024, // ~2MB for audio
    videoGeneration: 10 * 1024 * 1024, // ~10MB for video
    // Text-only purposes don't generate files
    characterDescription: 0,
    campaignDescription: 0,
    characterImport: 0,
    audioTranscription: 0
  }
  return estimates[purpose] || 0
}

/**
 * Calculate total size of input files in payload
 * @param {Array} files - Array of file objects with base64 data
 * @returns {number} Total size in bytes
 */
function calculateInputFilesSize(files) {
  if (!Array.isArray(files) || !files.length) return 0

  let totalBytes = 0
  for (const file of files) {
    const b64 = file?.data || file?.b64 || ''
    if (b64 && typeof b64 === 'string') {
      // Base64 size ≈ (encoded length * 3/4) for actual bytes
      totalBytes += Math.floor((b64.length * 3) / 4)
    }
  }
  return totalBytes
}

/**
 * POST-FLIGHT: Check storage quota for AI result after generation
 * This validates the actual result size and flags if quota is exceeded
 * @param {Object} result - AI job result containing URLs or data
 * @param {string} purpose - Purpose of the AI call
 * @returns {Promise<Object>} - { hasQuota: boolean, sizeMB: number, reason?: string, details?: Object }
 */
export async function checkStorageQuotaForResult(result, purpose = '') {
  try {
    const userStore = useUserStore()
    const userId = userStore?.user?.uid

    if (!userId) {
      return {
        hasQuota: false,
        sizeMB: 0,
        reason: 'User not authenticated'
      }
    }

    // Calculate actual or estimated size
    let totalSizeMB = 0

    // For media outputs, use conservative estimates based on purpose
    // (actual file sizes from Firebase Storage URLs can't be determined client-side)
    if (purpose) {
      const p = String(purpose).toLowerCase()
      if (p.includes('avatar')) {
        totalSizeMB = 5.0 // ~5MB for avatar (pre-minification)
      } else if (p.includes('gallery') || p.includes('campaign')) {
        totalSizeMB = 6.0 // ~6MB for high-res images (pre-minification)
      } else if (p.includes('video')) {
        totalSizeMB = 20.0 // ~20MB for video
      } else if (p.includes('image')) {
        totalSizeMB = 5.0 // ~5MB default for images
      } else if (p.includes('audio')) {
        totalSizeMB = 3.0 // ~3MB for audio
      }
    }

    // If no size estimate (text-only result), allow it
    if (totalSizeMB === 0) {
      return { hasQuota: true, sizeMB: 0 }
    }

    // Convert MB to bytes for quota check
    const totalSizeBytes = totalSizeMB * 1024 * 1024

    // Check storage quota
    const quotaCheck = await StorageQuotaService.checkQuota(userId, totalSizeBytes)

    if (!quotaCheck.allowed) {
      return {
        hasQuota: false,
        sizeMB: totalSizeMB,
        reason: quotaCheck.reason,
        details: {
          usedMB: parseFloat(quotaCheck.storageInfo?.usedMB || 0),
          limitMB: parseFloat(quotaCheck.storageInfo?.limitMB || 0),
          requiredMB: totalSizeMB,
          availableMB: parseFloat(quotaCheck.storageInfo?.available / (1024 * 1024) || 0)
        }
      }
    }

    return {
      hasQuota: true,
      sizeMB: totalSizeMB,
      details: {
        usedMB: parseFloat(quotaCheck.storageInfo?.usedMB || 0),
        limitMB: parseFloat(quotaCheck.storageInfo?.limitMB || 0),
        requiredMB: totalSizeMB,
        availableMB: parseFloat(quotaCheck.storageInfo?.available / (1024 * 1024) || 0)
      }
    }
  } catch (error) {
    console.error('[aiService] checkStorageQuotaForResult error:', error)
    return {
      hasQuota: false,
      sizeMB: 0,
      reason: error.message || 'Failed to check storage quota'
    }
  }
}

/**
 * aiService - thin wrapper around cloud callables + job-watcher helpers.
 * - enqueue(payload) => { jobId }
 * - enqueueAndWait(payload, timeoutMs) => normalized job result or throw on error/timeout
 * - watchJob(jobId, onUpdate) => returns unsubscribe()
 */

export async function enqueue(payload, options = {}) {
  const timeoutMs = options.timeoutMs || 120000

  // PRE-FLIGHT STORAGE QUOTA CHECK
  // Check if this AI request involves files (input or output) and validate storage quota
  const userStore = useUserStore()
  const userId = userStore?.user?.uid

  if (userId) {
    const inputFilesSize = calculateInputFilesSize(payload.files)
    const estimatedOutputSize = estimateOutputSize(payload.purpose)
    const totalEstimatedSize = inputFilesSize + estimatedOutputSize

    // Only check storage if files are involved
    if (totalEstimatedSize > 0) {
      try {
        const quotaCheck = await StorageQuotaService.checkQuota(userId, totalEstimatedSize)
        if (!quotaCheck.allowed) {
          const error = new Error(quotaCheck.reason)
          error.storageQuotaExceeded = true
          throw error
        }
      } catch (err) {
        // If it's our storage quota error, re-throw it
        if (err.storageQuotaExceeded || err.message?.toLowerCase().includes('storage')) {
          throw err
        }
        // Otherwise log and continue (don't block AI calls due to storage check failures)
        console.warn('[aiService] Storage quota check failed (non-fatal):', err)
      }
    }
  }
  // END PRE-FLIGHT CHECK
  // Sanitize prompt and any string fields in payload that may contain HTML
  const safePayload = { ...payload }
  // Helper: recursively sanitize any string fields inside objects/arrays
  function sanitizeStringsIn(obj) {
    if (obj == null) return obj
    if (typeof obj === 'string') {
      try {
        return sanitizeHtml(obj).replace(/<[^>]*>/g, '')
      } catch {
        return obj
      }
    }
    if (Array.isArray(obj)) {
      return obj.map((v) => sanitizeStringsIn(v))
    }
    if (typeof obj === 'object') {
      const out = {}
      for (const k of Object.keys(obj)) {
        try {
          // Avoid sanitizing obvious binary/file payloads
          if (
            k === 'data' &&
            typeof obj[k] === 'string' &&
            obj[k].length > 100 &&
            obj[k].match(/^[A-Za-z0-9+/]+=*$/)
          ) {
            out[k] = obj[k]
            continue
          }
          out[k] = sanitizeStringsIn(obj[k])
        } catch {
          out[k] = obj[k]
        }
      }
      return out
    }
    return obj
  }
  try {
    if (safePayload.prompt && typeof safePayload.prompt === 'string') {
      // Strip HTML tags but preserve basic text
      safePayload.prompt = sanitizeHtml(safePayload.prompt).replace(/<[^>]*>/g, '')
    }
    if (safePayload.context) {
      if (typeof safePayload.context === 'string') {
        safePayload.context = sanitizeHtml(safePayload.context).replace(/<[^>]*>/g, '')
      } else if (typeof safePayload.context === 'object') {
        // Deep-sanitize string fields inside structured contexts (but avoid binary file blobs)
        safePayload.context = sanitizeStringsIn(safePayload.context)
      }
    }
  } catch {
    // If sanitizer fails, fall back to original payload
    console.warn('aiService: sanitize failed')
  }

  try {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[aiService] enqueue prepared payload', {
        purpose: safePayload?.purpose,
        provider: safePayload?.provider,
        model: safePayload?.model,
        disableSystem: !!safePayload?.disableSystem,
        hasFiles: Array.isArray(safePayload?.files) && safePayload.files.length > 0,
        fileMeta: (safePayload?.files || []).map((f, idx) => ({
          idx,
          name: f?.name,
          type: f?.type,
          size: f?.data ? f.data.length : 0
        }))
      })
    }
  } catch {
    /* ignore logging errors */
  }

  const callable = httpsCallable(functions, 'aiQueue', { timeout: timeoutMs })

  // Retry/backoff for transient server-side throttling (429) and availability errors.
  const RETRYABLE_CODES = new Set([
    'resource-exhausted',
    'unavailable',
    'aborted',
    'deadline-exceeded'
  ])
  const maxAttempts = Number(options.attempts || 3)
  const baseDelay = Number(options.baseDelayMs || 500)

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms))

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = await callable(safePayload)
      try {
        if (typeof console !== 'undefined' && console.debug) {
          console.debug('[aiService] enqueue job accepted', {
            jobId: res?.data?.jobId,
            attempt,
            purpose: safePayload?.purpose
          })
        }
      } catch {
        /* ignore logging errors */
      }
      return res?.data || {}
    } catch (err) {
      const code = err && err.code ? String(err.code) : ''
      const msg = err && err.message ? String(err.message) : ''
      const is429 = /429/.test(msg) || /Too Many Requests/i.test(msg)
      const shouldRetry = is429 || RETRYABLE_CODES.has(code)
      if (attempt >= maxAttempts || !shouldRetry) {
        throw err
      }
      // Exponential backoff with jitter
      const delay = Math.floor(baseDelay * Math.pow(2, attempt - 1))
      const jitter = Math.floor(Math.random() * Math.min(300, delay))
      await sleep(delay + jitter)
      // continue to next attempt
    }
  }
  // Should never reach here
  return {}
}

export async function enqueueAndWait(payload, opts = {}) {
  const enqueueRes = await enqueue(payload, { timeoutMs: opts.enqueueTimeoutMs })
  const jobId = enqueueRes?.jobId
  if (!jobId) throw new Error('aiQueue did not return a jobId')
  try {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[aiService] enqueueAndWait awaiting job', {
        jobId,
        purpose: payload?.purpose,
        provider: payload?.provider,
        model: payload?.model
      })
    }
  } catch {
    /* ignore logging errors */
  }
  return await waitForJob(
    jobId,
    opts.waitTimeoutMs || Math.max(300000, opts.waitTimeoutMs || 300000)
  )
}

// Enqueue a job, stream progress updates, then wait for completion.
export async function enqueueAndWatch(payload, opts = {}) {
  const enqueueRes = await enqueue(payload, { timeoutMs: opts.enqueueTimeoutMs })
  const jobId = enqueueRes?.jobId
  if (!jobId) throw new Error('aiQueue did not return a jobId')

  let unsubProgress = null
  let unsubUpdate = null
  if (typeof opts.onProgress === 'function') {
    unsubProgress = watchJobProgress(
      jobId,
      (normalized, job) => opts.onProgress(normalized, job, jobId),
      opts
    )
  }
  if (typeof opts.onUpdate === 'function') {
    unsubUpdate = watchJob(jobId, (job) => opts.onUpdate(job, jobId))
  }

  try {
    return await waitForJob(
      jobId,
      opts.waitTimeoutMs || Math.max(300000, opts.waitTimeoutMs || 300000)
    )
  } finally {
    try {
      if (unsubProgress) unsubProgress()
    } catch {
      /* ignore */
    }
    try {
      if (unsubUpdate) unsubUpdate()
    } catch {
      /* ignore */
    }
  }
}

export function watchJob(jobId, onUpdate) {
  if (!jobId) throw new Error('jobId required')
  const ref = doc(db, 'aiJobs', jobId)
  const unsub = onSnapshot(ref, (snap) => {
    onUpdate(snap.exists() ? snap.data() : null)
  })
  return unsub
}

// Normalize raw job.progress into a small shape useful for UI consumption
export function normalizeProgress(raw) {
  if (!raw) return null
  try {
    const percent = Math.max(0, Math.min(100, Number(raw.percent || 0)))
    const message = raw.message || ''
    let label = ''
    const m = String(message || '').toLowerCase()
    if (m.includes('upload') || m.includes('uploading') || m.includes('uploading images')) {
      label = 'Uploading'
    } else if (m.includes('sending') || m.includes('prepared') || m.includes('prepared')) {
      label = 'Sending'
    } else if (percent < 20) {
      label = 'Preparing'
    } else if (percent < 50) {
      label = 'Waiting for model'
    } else if (percent < 80) {
      label = 'Processing'
    } else if (percent < 95) {
      label = 'Finishing'
    } else {
      label = 'Finalizing'
    }
    return { percent, message: message || undefined, label }
  } catch {
    return null
  }
}

// Debounced watcher for job progress. Calls onProgress(normalizedProgress, rawJob)
export function watchJobProgress(jobId, onProgress, opts = {}) {
  if (!jobId) throw new Error('jobId required')
  const debounceMs = Number(opts.debounceMs || 250)
  const ref = doc(db, 'aiJobs', jobId)
  let timer = null
  let lastSent = null
  let quotaCheckInProgress = false

  const unsub = onSnapshot(ref, async (snap) => {
    const job = snap.exists() ? snap.data() : null
    const normalized = normalizeProgress(job ? job.progress : null)

    // POST-FLIGHT STORAGE CHECK: If job completed and has result, check storage quota
    if (
      job &&
      job.status === 'completed' &&
      job.result &&
      job.purpose &&
      !quotaCheckInProgress &&
      !job.result?.storageQuota
    ) {
      quotaCheckInProgress = true
      try {
        const quotaStatus = await checkStorageQuotaForResult(job.result, job.purpose)
        // Attach quota status to job result (non-persisted, client-side only)
        if (job.result) {
          job.result.storageQuota = quotaStatus
        }
        console.log('[aiService] POST-FLIGHT storage check:', quotaStatus)
      } catch (error) {
        console.error('[aiService] POST-FLIGHT storage check failed:', error)
      } finally {
        quotaCheckInProgress = false
      }
    }

    // If nothing changed (progress OR status), do nothing. Include job.status so
    // status transitions (completed/failed) still trigger the callback even when
    // progress is unchanged.
    const keyObj = job ? { progress: job.progress || null, status: job.status || null } : null
    const key = keyObj ? JSON.stringify(keyObj) : null
    if (key === lastSent) return
    lastSent = key
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      try {
        onProgress(normalized, job)
      } catch {
        /* swallow */
      }
      timer = null
    }, debounceMs)
  })
  return () => {
    if (timer) clearTimeout(timer)
    unsub()
  }
}

// Helper to show a friendly notification for AI job errors
let _lastAiNotify = null
let _lastAiNotifyTimer = null
export function notifyAiError(err, opts = {}) {
  try {
    // Log for debugging
    console.log('[aiService.notifyAiError] Raw error:', err)

    // Try to handle as quota error first with centralized handler
    const wasQuotaError = handleQuotaError(err)
    if (wasQuotaError) {
      // Quota error was handled with upgrade CTA, don't show duplicate notification
      return 'Quota exceeded'
    }

    // Not a quota error, handle as regular AI error
    let friendly = null
    if (!err) friendly = 'AI job failed'
    else if (typeof err === 'string') friendly = err
    else if (err.message) friendly = err.message
    else if (err.error && typeof err.error === 'string') friendly = err.error
    else if (err.error && err.error.message) friendly = err.error.message
    else friendly = String(err)

    // Deduplicate repeated messages for a short window
    if (_lastAiNotify && String(_lastAiNotify).trim() === String(friendly).trim()) {
      return friendly
    }

    _lastAiNotify = friendly
    if (_lastAiNotifyTimer) clearTimeout(_lastAiNotifyTimer)
    _lastAiNotifyTimer = setTimeout(() => {
      _lastAiNotify = null
      _lastAiNotifyTimer = null
    }, 8000)

    const caption =
      String(friendly).trim() === 'empty_result' || (err && err === 'empty_result')
        ? 'No tokens were charged to your account.'
        : undefined
    const notifyOpts = {
      color: 'negative',
      message: friendly,
      timeout: 8000,
      icon: 'error',
      ...opts
    }
    if (caption) notifyOpts.caption = caption
    Notify.create(notifyOpts)
    return friendly
  } catch {
    try {
      Notify.create({ color: 'negative', message: 'AI job failed', timeout: 8000 })
    } catch (err2) {
      console.warn('aiService.notifyAiError fallback failed', err2)
    }
    return null
  }
}

async function waitForJob(jobId, timeoutMs = 300000) {
  return new Promise((resolve, reject) => {
    const ref = doc(db, 'aiJobs', jobId)
    let unsub = null
    try {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[aiService] waitForJob start', { jobId, timeoutMs })
      }
    } catch {
      /* ignore logging errors */
    }
    const timer = setTimeout(
      () => {
        if (unsub) unsub()
        try {
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[aiService] waitForJob timed out', { jobId, timeoutMs })
          }
        } catch {
          /* ignore logging errors */
        }
        reject(new Error('AI job timed out'))
      },
      Math.max(10000, timeoutMs)
    )
    unsub = onSnapshot(ref, async (snap) => {
      if (!snap.exists()) return
      const job = snap.data() || {}
      try {
        if (typeof console !== 'undefined' && console.debug) {
          console.debug('[aiService] waitForJob update', {
            jobId,
            status: job.status,
            progress: job.progress?.percent || null
          })
        }
      } catch {
        /* ignore logging errors */
      }
      if (job.status === 'completed') {
        clearTimeout(timer)
        if (unsub) unsub()

        // POST-FLIGHT STORAGE CHECK: Check storage quota for completed job
        if (job.result && job.purpose) {
          try {
            const quotaStatus = await checkStorageQuotaForResult(job.result, job.purpose)
            // Attach quota status to result
            job.result.storageQuota = quotaStatus
            console.log('[aiService] POST-FLIGHT storage check for waitForJob:', quotaStatus)
          } catch (error) {
            console.error('[aiService] POST-FLIGHT storage check failed in waitForJob:', error)
          }
        }

        try {
          if (typeof console !== 'undefined' && console.debug) {
            console.debug('[aiService] waitForJob completed', {
              jobId,
              hasResult: Boolean(job.result)
            })
          }
        } catch {
          /* ignore logging errors */
        }
        resolve(job.result || {})
      } else if (job.status === 'failed') {
        clearTimeout(timer)
        if (unsub) unsub()
        try {
          if (typeof console !== 'undefined' && console.error) {
            console.error('[aiService] waitForJob failed', { jobId, error: job.error })
          }
        } catch {
          /* ignore logging errors */
        }
        reject(new Error(job.error || 'AI job failed'))
      }
    })
  })
}
